import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';

import {
  Cancel as CancelIcon,
  ContentCopyOutlined as ContentCopyOutlinedIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import { deleteBroadcast, fetchBroadcast, stopBroadcast } from 'store/actions/broadcasts';
import { broadcastsSelectors } from 'store/selectors/broadcasts';
import { setSnackbarError, setSnackbarSuccess, setSnackbarWarning } from 'store/actions/global';

import { cancellationStatuses, statuses } from 'utils/constants';
import { renderCreatedBy, renderSendTime } from 'utils/messages';
import { timeFormatOptions } from 'utils/timezones';

import TopCard from 'common/containers/TopCard';

const chipColor = (value, sendAtUtc) => {
  const { draft, published } = statuses;

  switch (value) {
    case published: {
      const sent = new Date().getTime() - new Date(sendAtUtc).getTime() > 0;

      return sent ? 'secondary' : 'blue';
    }
    case draft:
      return 'grey';
    default:
      return null;
  }
};

const chipLabel = (value, sendAtUtc) => {
  const { draft, published } = statuses;

  switch (value) {
    case published: {
      const sent = new Date().getTime() - new Date(sendAtUtc).getTime() > 0;

      return sent ? 'Sent' : 'Scheduled';
    }
    case draft:
      return 'Draft';
    default:
      return null;
  }
};

const BroadcastTopCard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const confirm = useConfirm();
  const { draft } = statuses;
  const { probablyCancelled } = cancellationStatuses;
  const { broadcastUuid, organizationUuid } = useParams();

  const { broadcastDetails } = useSelector(state => ({
    broadcastDetails: broadcastsSelectors.getBroadcastDetails(state),
  }));

  const {
    contactLists,
    createdAt,
    createdBy,
    isCancellable,
    name,
    message,
    subject,
    sendAt,
    sendAtUtc,
    status,
    updatedAt,
  } = broadcastDetails;

  const [isMessageOpen, setMessageOpen] = useState(false);

  const handleMessageClose = () => setMessageOpen(false);

  const handleEditBroadcast = () => {
    history.push(`/${organizationUuid}/${broadcastUuid}/create-email-broadcast`);
  };

  const handleDuplicateBroadcast = () => {
    history.push(`/${organizationUuid}/create-email-broadcast/${broadcastUuid}`);
  };

  const handleDeleteBroadcast = () => {
    confirm({
      confirmationText: 'Delete',
      description: 'Deleting this broadcast will also remove any related recipient analytics.',
      title: 'Delete the Broadcast?',
    })
      .then(() => dispatch(deleteBroadcast(broadcastUuid)))
      .then(() => history.push(`/${organizationUuid}/broadcasts`));
  };

  const handleStopBroadcast = () => {
    dispatch(stopBroadcast(broadcastUuid))
      .then(({ message: stopedBroadcastMessage, status: stopedBroadcastStatus }) => {
        if (stopedBroadcastStatus === probablyCancelled) {
          dispatch(setSnackbarWarning({ message: stopedBroadcastMessage }));
        } else {
          dispatch(setSnackbarSuccess({ message: stopedBroadcastMessage }));
        }
        dispatch(fetchBroadcast(broadcastUuid));
      })
      .catch(({ message: errorMessage }) => dispatch(setSnackbarError({ message: errorMessage })));
  };

  const handleConfirmStopBroadcast = () => {
    confirm({
      cancellationText: 'No',
      confirmationText: 'Yes, Cancel',
      description: '',
      title: 'Cancel send the Broadcast?',
    }).then(() => handleStopBroadcast(broadcastUuid));
  };

  const topCardData = useCallback(() => {
    const chipData = {
      chipColor: chipColor(status, sendAtUtc),
      chipLabel: chipLabel(status, sendAtUtc),
      chipOptions: [],
      chipOptionsHeader: 'Change status',
    };

    const actionButtons = [
      {
        color: 'grey',
        disabled: status !== draft,
        name: 'Edit',
        onClick: handleEditBroadcast,
        startIcon: <EditOutlinedIcon />,
      },
      {
        color: 'grey',
        name: 'Duplicate',
        onClick: handleDuplicateBroadcast,
        startIcon: <ContentCopyOutlinedIcon />,
      },
      {
        color: 'grey',
        name: 'Delete',
        onClick: handleDeleteBroadcast,
        startIcon: <DeleteOutlineOutlinedIcon />,
      },
    ];

    if (isCancellable) {
      actionButtons.unshift({
        color: 'grey',
        disabled: false, // TODO adapt this based on scheduled time
        name: 'Cancel send',
        onClick: handleConfirmStopBroadcast,
        startIcon: <CancelIcon />,
      });
    }

    const additionalActionButtons = [
      {
        color: 'primary',
        endIcon: <OpenInNewIcon />,
        name: 'Preview message',
        onClick: () => setMessageOpen(true),
      },
    ];

    const details = [
      {
        label: 'Subject',
        value: subject,
      },
      ...renderSendTime(status, sendAt, sendAtUtc, updatedAt),
      {
        label: 'Created at',
        value: `${new Date(createdAt).toLocaleString('us-US', timeFormatOptions)}`,
      },
      ...renderCreatedBy(createdBy),
      {
        label: 'Lists',
        value: contactLists?.map(({ name: listName }) => listName).join(', '),
      },
    ];

    return {
      actionButtons,
      additionalActionButtons,
      backButtonLink: `/${organizationUuid}/broadcasts`,
      chipData,
      details,
      entityTypes: ['Broadcast', 'Email Message'],
      title: name || '',
    };
  }, [name, status, draft, isCancellable, probablyCancelled, organizationUuid]);

  return (
    <>
      <TopCard {...topCardData()} />
      <Dialog
        aria-labelledby="max-width-dialog-message"
        maxWidth="lg"
        open={isMessageOpen}
        onClose={handleMessageClose}
      >
        <DialogTitle id="max-width-dialog-message">{subject}</DialogTitle>
        <DialogContent dangerouslySetInnerHTML={{ __html: message }} />
        <DialogActions>
          <Button onClick={handleMessageClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BroadcastTopCard;
